import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
	CorrectionSidebarItem,
	PlantingAdvice,
	CalagemFormData,
	CalagemAjusteData,
	CalagemResult,
	FosfatagemFormData,
	FosfatagemAjusteData,
	FosfatagemResult,
	GessagemFormData,
	GessagemAjusteData,
	GessagemResult,
	RecommendationTabData,
	RecommendationMetadata,
	ResultTableRow,
	CorrectionStartResponse,
} from '../types';
import {
	DEFAULT_CALAGEM_FORM,
	DEFAULT_FOSFATAGEM_FORM,
	DEFAULT_GESSAGEM_FORM,
	DEFAULT_RECOMMENDATION_TAB,
} from '../constants/correction.constants';

type FormaAplicacaoKey = 'total' | 'faixa' | 'sulco' | 'cova';

const asString = (value: unknown): string => {
	if (value === null || value === undefined) return '';
	if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
	if (typeof value === 'string') return value;
	if (typeof value === 'object') {
		const record = value as Record<string, unknown>;
		return asString(record._id ?? record.id ?? record.name);
	}
	return String(value);
};

const asDisplayName = (value: unknown): string => {
	if (value && typeof value === 'object') {
		const record = value as Record<string, unknown>;
		return asString(record.name ?? record.title ?? record._id ?? record.id);
	}
	return asString(value);
};

const numberString = (value: unknown): string => {
	if (value === null || value === undefined || value === '') return '';
	const numeric = Number(value);
	return Number.isFinite(numeric) ? String(numeric) : asString(value);
};

const findMetadata = (
	advice: PlantingAdvice,
	resource: string
): RecommendationMetadata | undefined =>
	advice.metadatas?.find((metadata) => metadata.resource === resource);

const getFormaConfig = (
	advice: PlantingAdvice,
	metadata: RecommendationMetadata | undefined,
	forma: string | undefined
) => {
	const metaConfig = metadata?.meta?.formaAplicacao;
	if (metaConfig) return metaConfig;
	if (!forma) return undefined;
	return advice.formasAplicacao?.[forma as FormaAplicacaoKey];
};

const mapMetodo = (metodo: string | undefined): CalagemFormData['metodo'] => {
	if (metodo === 'CaMg2' || metodo === 'CaMg') return 'CaMg';
	if (metodo === 'satCa2' || metodo === 'satSB') return 'satSB';
	return '';
};

const formatFormaAplicacao = (forma: string | undefined, value: unknown): string => {
	const formattedValue = numberString(value);
	if (!formattedValue) return '—';
	const labels: Record<string, string> = {
		total: `${formattedValue} kg/ha`,
		sulco: `${formattedValue} g/m`,
		faixa: `${formattedValue} kg/ha na faixa`,
		cova: `${formattedValue} g/cova`,
	};
	return labels[forma ?? ''] ?? formattedValue;
};

const makeResultRow = (
	param: string,
	unit: string,
	after: unknown,
	current = '—',
	range = '—'
): ResultTableRow => ({
	param,
	unit,
	current,
	range,
	after: numberString(after) || '—',
});

type ResultadoKey = keyof NonNullable<PlantingAdvice['resultado']>;

const RESULT_ROW_CONFIG: Record<
	ResultadoKey,
	{ param: string; unit: string; startKeys?: string[] }
> = {
	K: { param: 'K+', unit: 'mg/dm³' },
	satK: { param: 'Sat. K', unit: '%' },
	Pm1: { param: 'P-M1', unit: 'mg/dm³' },
	pHagua: { param: 'pH água', unit: '' },
	Ca: { param: 'Ca', unit: 'cmolc/dm³' },
	Mg: { param: 'Mg', unit: 'cmolc/dm³' },
	satCa: { param: 'Sat. Ca', unit: '%' },
	satMg: { param: 'Sat. Mg', unit: '%' },
	satSB: { param: 'Sat. bases', unit: '%' },
	S_g: { param: 'S_g', unit: 'mg/dm³' },
	Ca_g: { param: 'Ca_g', unit: 'cmolc/dm³', startKeys: ['Ca_g', 'Ca'] },
	satCa_g: { param: 'satCa_g', unit: '%', startKeys: ['satCa_g', 'satCa'] },
};

const getStartKeys = (key: ResultadoKey): string[] => RESULT_ROW_CONFIG[key]?.startKeys ?? [key];

const getStartCurrent = (
	startResult: CorrectionStartResponse | undefined,
	key: ResultadoKey
): string => {
	for (const startKey of getStartKeys(key)) {
		const value = numberString(startResult?.atual?.[startKey]);
		if (value) return value;
	}
	return '—';
};

const getStartRange = (
	startResult: CorrectionStartResponse | undefined,
	key: ResultadoKey
): string => {
	for (const startKey of getStartKeys(key)) {
		const value = startResult?.faixaAdequada?.[startKey];
		if (value) return value;
	}
	return '—';
};

const buildCorrectionResultTable = (
	advice: PlantingAdvice,
	keys: ResultadoKey[],
	startResult?: CorrectionStartResponse
): ResultTableRow[] => {
	const resultado = advice.resultado;
	if (!resultado) return [];

	return keys.map((key) => {
		const label = RESULT_ROW_CONFIG[key] ?? { param: String(key), unit: '' };
		return makeResultRow(
			label.param,
			label.unit,
			resultado[key],
			getStartCurrent(startResult, key),
			getStartRange(startResult, key)
		);
	});
};

const buildRecommendationResultTable = (
	advice: PlantingAdvice,
	startResult?: CorrectionStartResponse
): ResultTableRow[] =>
	buildCorrectionResultTable(
		advice,
		['K', 'satK', 'Pm1', 'pHagua', 'Ca', 'Mg', 'satCa', 'satMg', 'satSB', 'S_g', 'Ca_g', 'satCa_g'],
		startResult
	);

export type DirtyCorrectionCalculation = 'calagem' | 'fosfatagem' | 'gessagem';

const markCalculationAsDirty = (
	dirtyCalculations: DirtyCorrectionCalculation[],
	calculation: DirtyCorrectionCalculation
) =>
	dirtyCalculations.includes(calculation)
		? dirtyCalculations
		: [...dirtyCalculations, calculation];

const hasProductReference = (product: { productId?: string; product?: unknown } | undefined) =>
	!!product && !!(product.productId || asString(product.product));

export interface CorrectionWorkspaceState {
	advice: PlantingAdvice | null;
	correctionStart: CorrectionStartResponse | null;
	activeSidebarItem: CorrectionSidebarItem;
	hasUnsavedChanges: boolean;
	dirtyCalculations: DirtyCorrectionCalculation[];

	calagem: CalagemFormData;
	calagemAjuste: CalagemAjusteData;
	calagemResult: CalagemResult | null;
	calagemHasCalculated: boolean;

	fosfatagem: FosfatagemFormData;
	fosfatagemAjuste: FosfatagemAjusteData;
	fosfatagemResult: FosfatagemResult | null;
	fosfatagemHasCalculated: boolean;

	gessagem: GessagemFormData;
	gessagemAjuste: GessagemAjusteData;
	gessagemResult: GessagemResult | null;
	gessagemHasCalculated: boolean;

	recommendation: RecommendationTabData;

	setAdvice: (advice: PlantingAdvice) => void;
	setActiveSidebarItem: (item: CorrectionSidebarItem) => void;

	setCalagemField: (key: keyof CalagemFormData, value: string) => void;
	setCalagemAjusteField: (key: keyof CalagemAjusteData, value: string) => void;
	setCalagemResult: (result: CalagemResult | null) => void;
	setCalagemHasCalculated: (v: boolean) => void;
	resetCalagem: () => void;

	setFosfatagemField: (key: keyof FosfatagemFormData, value: string) => void;
	setFosfatagemAjusteField: (key: keyof FosfatagemAjusteData, value: string) => void;
	setFosfatagemResult: (result: FosfatagemResult | null) => void;
	setFosfatagemHasCalculated: (v: boolean) => void;
	resetFosfatagem: () => void;

	setGessagemField: (key: keyof GessagemFormData, value: string) => void;
	setGessagemAjusteField: (key: keyof GessagemAjusteData, value: string) => void;
	setGessagemResult: (result: GessagemResult | null) => void;
	setGessagemHasCalculated: (v: boolean) => void;
	resetGessagem: () => void;

	setRecommendationField: (key: keyof RecommendationTabData, value: string) => void;

	preloadFromAdvice: (advice: PlantingAdvice, startResult?: CorrectionStartResponse) => void;
	reset: () => void;
}

const initialState = {
	advice: null,
	correctionStart: null,
	activeSidebarItem: 'general' as CorrectionSidebarItem,
	hasUnsavedChanges: false,
	dirtyCalculations: [] as DirtyCorrectionCalculation[],

	calagem: { ...DEFAULT_CALAGEM_FORM },
	calagemAjuste: { dose: '', doseSecundaria: '' },
	calagemResult: null,
	calagemHasCalculated: false,

	fosfatagem: { ...DEFAULT_FOSFATAGEM_FORM },
	fosfatagemAjuste: { dose: '', doseSecundaria: '' },
	fosfatagemResult: null,
	fosfatagemHasCalculated: false,

	gessagem: { ...DEFAULT_GESSAGEM_FORM },
	gessagemAjuste: { dose: '', doseSecundaria: '' },
	gessagemResult: null,
	gessagemHasCalculated: false,

	recommendation: { ...DEFAULT_RECOMMENDATION_TAB },
};

export const useCorrectionStore = create<CorrectionWorkspaceState>()(
	devtools(
		(set) => ({
			...initialState,

			setAdvice: (advice) =>
				set({ advice, hasUnsavedChanges: false, dirtyCalculations: [] }),
			setActiveSidebarItem: (item) => set({ activeSidebarItem: item }),

			setCalagemField: (key, value) =>
				set((s) => ({
					calagem: { ...s.calagem, [key]: value },
					hasUnsavedChanges: true,
					dirtyCalculations: markCalculationAsDirty(s.dirtyCalculations, 'calagem'),
				})),
			setCalagemAjusteField: (key, value) =>
				set((s) => ({
					calagemAjuste: { ...s.calagemAjuste, [key]: value },
					hasUnsavedChanges: true,
					dirtyCalculations: markCalculationAsDirty(s.dirtyCalculations, 'calagem'),
				})),
			setCalagemResult: (result) =>
				set((s) => ({
					calagemResult: result,
					hasUnsavedChanges: true,
					dirtyCalculations: markCalculationAsDirty(s.dirtyCalculations, 'calagem'),
				})),
			setCalagemHasCalculated: (v) =>
				set((s) => ({
					calagemHasCalculated: v,
					hasUnsavedChanges: true,
					dirtyCalculations: markCalculationAsDirty(s.dirtyCalculations, 'calagem'),
				})),
			resetCalagem: () =>
				set((s) => ({
					calagem: { ...DEFAULT_CALAGEM_FORM },
					calagemAjuste: { dose: '', doseSecundaria: '' },
					calagemResult: null,
					calagemHasCalculated: false,
					hasUnsavedChanges: true,
					dirtyCalculations: markCalculationAsDirty(s.dirtyCalculations, 'calagem'),
				})),

			setFosfatagemField: (key, value) =>
				set((s) => ({
					fosfatagem: { ...s.fosfatagem, [key]: value },
					hasUnsavedChanges: true,
					dirtyCalculations: markCalculationAsDirty(s.dirtyCalculations, 'fosfatagem'),
				})),
			setFosfatagemAjusteField: (key, value) =>
				set((s) => ({
					fosfatagemAjuste: { ...s.fosfatagemAjuste, [key]: value },
					hasUnsavedChanges: true,
					dirtyCalculations: markCalculationAsDirty(s.dirtyCalculations, 'fosfatagem'),
				})),
			setFosfatagemResult: (result) =>
				set((s) => ({
					fosfatagemResult: result,
					hasUnsavedChanges: true,
					dirtyCalculations: markCalculationAsDirty(s.dirtyCalculations, 'fosfatagem'),
				})),
			setFosfatagemHasCalculated: (v) =>
				set((s) => ({
					fosfatagemHasCalculated: v,
					hasUnsavedChanges: true,
					dirtyCalculations: markCalculationAsDirty(s.dirtyCalculations, 'fosfatagem'),
				})),
			resetFosfatagem: () =>
				set((s) => ({
					fosfatagem: { ...DEFAULT_FOSFATAGEM_FORM },
					fosfatagemAjuste: { dose: '', doseSecundaria: '' },
					fosfatagemResult: null,
					fosfatagemHasCalculated: false,
					hasUnsavedChanges: true,
					dirtyCalculations: markCalculationAsDirty(s.dirtyCalculations, 'fosfatagem'),
				})),

			setGessagemField: (key, value) =>
				set((s) => ({
					gessagem: { ...s.gessagem, [key]: value },
					hasUnsavedChanges: true,
					dirtyCalculations: markCalculationAsDirty(s.dirtyCalculations, 'gessagem'),
				})),
			setGessagemAjusteField: (key, value) =>
				set((s) => ({
					gessagemAjuste: { ...s.gessagemAjuste, [key]: value },
					hasUnsavedChanges: true,
					dirtyCalculations: markCalculationAsDirty(s.dirtyCalculations, 'gessagem'),
				})),
			setGessagemResult: (result) =>
				set((s) => ({
					gessagemResult: result,
					hasUnsavedChanges: true,
					dirtyCalculations: markCalculationAsDirty(s.dirtyCalculations, 'gessagem'),
				})),
			setGessagemHasCalculated: (v) =>
				set((s) => ({
					gessagemHasCalculated: v,
					hasUnsavedChanges: true,
					dirtyCalculations: markCalculationAsDirty(s.dirtyCalculations, 'gessagem'),
				})),
			resetGessagem: () =>
				set((s) => ({
					gessagem: { ...DEFAULT_GESSAGEM_FORM },
					gessagemAjuste: { dose: '', doseSecundaria: '' },
					gessagemResult: null,
					gessagemHasCalculated: false,
					hasUnsavedChanges: true,
					dirtyCalculations: markCalculationAsDirty(s.dirtyCalculations, 'gessagem'),
				})),

			setRecommendationField: (key, value) =>
				set((s) => ({
					recommendation: { ...s.recommendation, [key]: value },
					hasUnsavedChanges: true,
				})),

			preloadFromAdvice: (advice, startResult) => {
				const updates: Partial<CorrectionWorkspaceState> = {
					advice,
					correctionStart: startResult ?? null,
					hasUnsavedChanges: false,
					dirtyCalculations: [],
					calagem: { ...DEFAULT_CALAGEM_FORM },
					calagemAjuste: { dose: '', doseSecundaria: '' },
					calagemResult: null,
					calagemHasCalculated: false,
					fosfatagem: { ...DEFAULT_FOSFATAGEM_FORM },
					fosfatagemAjuste: { dose: '', doseSecundaria: '' },
					fosfatagemResult: null,
					fosfatagemHasCalculated: false,
					gessagem: { ...DEFAULT_GESSAGEM_FORM },
					gessagemAjuste: { dose: '', doseSecundaria: '' },
					gessagemResult: null,
					gessagemHasCalculated: false,
					recommendation: { ...DEFAULT_RECOMMENDATION_TAB },
				};
				const calagemMetadata = findMetadata(advice, 'calcularCalagem');
				const adubacaoMetadata = findMetadata(advice, 'calcularAdubacao');
				const gessagemMetadata = findMetadata(advice, 'calcularGessagem');
				const hasSavedFosfatagem =
					!!advice.savedFosfatagem?.form.fertilizanteP &&
					!!advice.savedFosfatagem.form.forma;
				const hasPersistedFosfatagem =
					!!advice.adubacao?.forma && hasProductReference(advice.adubacao.products?.[0]);

				if (advice.savedCalagem) {
					updates.calagem = advice.savedCalagem.form;
					updates.calagemAjuste = advice.savedCalagem.ajuste;
					updates.calagemResult = advice.savedCalagem.result;
					updates.calagemHasCalculated = true;
				} else if (advice.calagem) {
					const formaConfig = getFormaConfig(advice, calagemMetadata, advice.calagem.forma);
					updates.calagem = {
						corretivo: asString(advice.calagem.product) || '',
						metodo: mapMetodo(advice.calagem.metodo),
						metodoValue: numberString(advice.calagem.metodoValue),
						forma: advice.calagem.forma,
						profundidade: numberString(formaConfig?.p),
						largura: numberString(formaConfig?.l),
						comprimento: numberString(formaConfig?.c),
					};
					updates.calagemAjuste = {
						dose: numberString(advice.calagem.dose),
						doseSecundaria: numberString(advice.calagem.formaValue),
					};
					updates.calagemResult = {
						ajuste: updates.calagemAjuste,
						table: buildCorrectionResultTable(
							advice,
							['Ca', 'Mg', 'satCa', 'satMg', 'satSB', 'pHagua'],
							startResult
						),
					};
					updates.calagemHasCalculated = true;
				}
				if (advice.savedFosfatagem && hasSavedFosfatagem) {
					updates.fosfatagem = advice.savedFosfatagem.form;
					updates.fosfatagemAjuste = advice.savedFosfatagem.ajuste;
					updates.fosfatagemResult = advice.savedFosfatagem.result;
					updates.fosfatagemHasCalculated = true;
				} else if (advice.adubacao && hasPersistedFosfatagem) {
					const [mainProduct, complementaryProduct] = advice.adubacao.products ?? [];
					const formaConfig = getFormaConfig(advice, adubacaoMetadata, advice.adubacao.forma);
					const totalN =
						advice.adubacao.products?.reduce((sum, product) => sum + (product.N ?? 0), 0) ?? 0;
					const totalP2O5 =
						advice.adubacao.products?.reduce((sum, product) => sum + (product.P2O5 ?? 0), 0) ?? 0;
					const totalK2O =
						advice.adubacao.products?.reduce((sum, product) => sum + (product.K2O ?? 0), 0) ?? 0;

					updates.fosfatagem = {
						fertilizanteP: asString(mainProduct?.product) || mainProduct?.productId || '',
						fertilizanteComplementar:
							asString(complementaryProduct?.product) || complementaryProduct?.productId || '',
						forma: advice.adubacao.forma,
						profundidade: numberString(formaConfig?.p),
						largura: numberString(formaConfig?.l),
						comprimento: numberString(formaConfig?.c),
					};
					updates.fosfatagemAjuste = {
						dose: numberString(mainProduct?.dose),
						doseSecundaria: numberString(mainProduct?.formaValue),
						doseComplementar: numberString(complementaryProduct?.dose),
						doseSecundariaComplementar: numberString(complementaryProduct?.formaValue),
					};
					updates.fosfatagemResult = {
						ajuste: updates.fosfatagemAjuste,
						products: (advice.adubacao.products ?? []).map((product) => ({
							productId: asString(product.product) || product.productId || '',
							dose: numberString(product.dose),
							formaValue: numberString(product.formaValue),
							N: numberString(product.N),
							P2O5: numberString(product.P2O5),
							K2O: numberString(product.K2O),
						})),
						table: buildCorrectionResultTable(advice, ['Pm1', 'K', 'satK'], startResult),
						N: numberString(totalN),
						P2O5: numberString(totalP2O5),
						K2O: numberString(totalK2O),
					};
					updates.fosfatagemHasCalculated = true;
				}
				if (advice.savedGessagem) {
					updates.gessagem = advice.savedGessagem.form;
					updates.gessagemAjuste = advice.savedGessagem.ajuste;
					updates.gessagemResult = advice.savedGessagem.result;
					updates.gessagemHasCalculated = true;
				} else if (advice.gessagem) {
					const formaConfig = getFormaConfig(advice, gessagemMetadata, advice.gessagem.forma);
					updates.gessagem = {
						soilAnalysis: asString(advice.gessagem.soilAnalysis || advice.soilAnalysis),
						interpretacao: advice.gessagem.interpretacao ?? '',
						condicionador: asString(advice.gessagem.product) || advice.gessagem.productId || '',
						satCa2: numberString(advice.gessagem.satCa2),
						forma: advice.gessagem.forma,
						profundidade: numberString(formaConfig?.p),
						largura: numberString(formaConfig?.l),
						comprimento: numberString(formaConfig?.c),
					};
					updates.gessagemAjuste = {
						dose: numberString(advice.gessagem.dose),
						doseSecundaria: numberString(advice.gessagem.formaValue),
					};
					updates.gessagemResult = {
						ajuste: updates.gessagemAjuste,
						table: buildCorrectionResultTable(advice, ['S_g', 'Ca_g', 'satCa_g'], startResult),
					};
					updates.gessagemHasCalculated = true;
				}
				if (advice.savedRecommendation) {
					updates.recommendation = {
						...DEFAULT_RECOMMENDATION_TAB,
						...advice.savedRecommendation,
					};
				} else {
					const products = [
						...(advice.calagem
							? [
									{
										product: asDisplayName(advice.calagem.product),
										dose: numberString(advice.calagem.dose),
										forma: formatFormaAplicacao(advice.calagem.forma, advice.calagem.formaValue),
									},
								]
							: []),
						...(advice.adubacao?.products?.map((product) => ({
							product: asDisplayName(product.product) || product.productId || '',
							productId: asString(product.product) || product.productId || '',
							dose: numberString(product.dose),
							forma: formatFormaAplicacao(advice.adubacao?.forma, product.formaValue),
						})) ?? []),
						...(advice.gessagem
							? [
									{
										product:
											asDisplayName(advice.gessagem.product) ||
											advice.gessagem.productId ||
											'',
										dose: numberString(advice.gessagem.dose),
										forma: formatFormaAplicacao(advice.gessagem.forma, advice.gessagem.formaValue),
									},
								]
							: []),
					];
					updates.recommendation = {
						identificacao: advice.name ?? '',
						observacoes: advice.note ?? '',
						products,
						resultTable: buildRecommendationResultTable(advice, startResult),
					};
				}
				if (Object.keys(updates).length > 0) set(updates);
			},

			reset: () => set(initialState),
		}),
		{ name: 'CorrectionWorkspaceStore' }
	)
);
